import React from "react";
import {
  AlertCircle,
  FileText,
  Clock,
  TrendingUp,
  Building,
  BarChart3,
  ClipboardCheck,
  Wrench,
  MessageSquare,
  Plus,
} from "lucide-react";
import { hasPermission } from "../../../config/navigation";
import LoadingSpinner from "../../common/LoadingSpinner";

const normalizeStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .trim();

const defaultStatusColor = (status) => {
  const s = normalizeStatus(status);
  if (s === "active") return "bg-green-100 text-green-800";
  if (s === "completed") return "bg-blue-100 text-blue-800";
  if (s === "on_hold" || s === "on-hold")
    return "bg-yellow-100 text-yellow-800";
  if (s === "cancelled" || s === "canceled") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-800";
};

const DashboardView = ({
  dashboardData,
  currentUser,
  isLoading,
  error,
  success,
  projects,
  filteredNcrs,
  activityFeed,
  kpis,
  charts,
  onQuickAction,
  getStatusColor,
  onOpenProject,
}) => {
  const userRole = currentUser?.role || "viewer";
  const canCreateProject = hasPermission(userRole, "create", "projects");
  const statusColorFn = getStatusColor || defaultStatusColor;

  const safeKpis = kpis || {};
  const safeInspections = safeKpis.inspections || {};
  const safeRfis = safeKpis.rfis || {};
  const safeSubmittals = safeKpis.submittals || {};

  const projectList = Array.isArray(projects) ? projects : [];
  const safeCharts = charts || {};

  const handleOpenProject = (project) => {
    if (typeof onOpenProject === "function") {
      onOpenProject(project);
    }
  };

  const last7 = safeCharts.inspections_last_7_days || [];
  const last7Max = last7.length
    ? Math.max(1, ...last7.map((d) => d.count || 0))
    : 1;

  const ncrLast7 = safeCharts.ncrs_last_7_days || [];
  const ncrLast7Max = ncrLast7.length
    ? Math.max(1, ...ncrLast7.map((d) => d.count || 0))
    : 1;

  const openRfis = safeRfis.open ?? safeRfis.pending ?? 0;
  const closedRfis = safeRfis.closed ?? 0;
  const pendingRfisKpi = safeRfis.pending ?? 0;
  const uploadedRfisKpi = safeRfis.uploaded ?? 0; // RFIs created via internal uploads

  const submittedSubmittals = safeSubmittals.submitted ?? 0;
  const approvedSubmittals = safeSubmittals.approved ?? 0;
  const rejectedSubmittals = safeSubmittals.rejected ?? 0;
  const uploadedSubmittalsKpi = safeSubmittals.uploaded ?? 0; // Submittals created via internal uploads
  const submittalsTotal =
    safeSubmittals.total ??
    submittedSubmittals +
      approvedSubmittals +
      rejectedSubmittals +
      uploadedSubmittalsKpi;

  const defaultDashboardData = {
    total_projects: projectList.length,
    active_projects: projectList.filter(
      (p) => normalizeStatus(p.status) === "active",
    ).length,
    total_inspections: safeKpis.total_inspections ?? 0,
    completed_inspections: safeKpis.completed_inspections ?? 0,
    open_ncrs: safeKpis.open_ncrs ?? 0,
    pending_rfis: safeKpis.pending_rfis ?? 0,
    critical_findings: safeKpis.critical_findings ?? 0,
  };

  const safeDashboardData = dashboardData || defaultDashboardData;

  const {
    total_projects = 0,
    active_projects = 0,
    total_inspections = 0,
    completed_inspections = 0,
    open_ncrs = 0,
  } = safeDashboardData;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">QA/QC Dashboard</h2>
          <p className="text-gray-600">
            Smoky Hill Engineering LLC - Quality Control Center
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">
            User: {currentUser?.first_name || "User"}
          </p>
          <p className="text-sm text-gray-600">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* KPI cards with inline status overviews */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Projects */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Projects</p>
              <p className="text-3xl font-bold text-gray-800">
                {total_projects}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {active_projects} active
              </p>
              <div className="mt-2 text-[10px] text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Active
                </span>
              </div>
            </div>
            <Building className="w-12 h-12 text-blue-500 opacity-50" />
          </div>
        </div>

        {/* Inspections */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Inspections</p>
              <p className="text-3xl font-bold text-gray-800">
                {total_inspections}
              </p>
              <p className="text-xs text-green-600 mt-1">
                {completed_inspections} completed
              </p>
              {/* Inline inspection status overview */}
              <div className="mt-2 text-[10px] text-gray-500 flex flex-wrap gap-x-2 gap-y-1">
                <span>Draft: {safeInspections.draft ?? 0}</span>
                <span>Submitted: {safeInspections.submitted ?? 0}</span>
                <span>Approved: {safeInspections.approved ?? 0}</span>
                <span>Rejected: {safeInspections.rejected ?? 0}</span>
                {/* <span>completed: {safeInspections.completed ?? 0}</span> */}
              </div>
            </div>
            <FileText className="w-12 h-12 text-green-500 opacity-50" />
          </div>
        </div>

        {/* NCRs */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Open NCRs</p>
              <p className="text-3xl font-bold text-gray-800">{open_ncrs}</p>
              <p className="text-xs text-orange-600 mt-1">Requires attention</p>
              {/* Simple inline hint */}
              <div className="mt-2 text-[10px] text-gray-500">
                High / Critical NCRs surface under Urgent Items
              </div>
            </div>
            <AlertCircle className="w-12 h-12 text-red-500 opacity-50" />
          </div>
        </div>

        {/* RFIs */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">RFIs</p>
              <p className="text-3xl font-bold text-gray-800">{openRfis}</p>
              <p className="text-xs text-yellow-600 mt-1">
                {closedRfis} closed • {pendingRfisKpi} pending •{" "}
                {uploadedRfisKpi} uploaded
              </p>
              {/* Inline RFI status overview */}
              <div className="mt-2 text-[10px] text-gray-500 flex flex-wrap gap-x-2 gap-y-1">
                <span>Open {safeRfis.open ?? safeRfis.pending ?? 0}</span>
                <span>Closed {safeRfis.closed ?? 0}</span>
                <span>Pending {safeRfis.pending ?? 0}</span>
                <span>Uploaded {safeRfis.uploaded ?? 0}</span>
              </div>
            </div>
            <MessageSquare className="w-12 h-12 text-yellow-500 opacity-50" />
          </div>
        </div>

        {/* Submittals */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Submittals</p>
              <p className="text-3xl font-bold text-gray-800">
                {submittalsTotal}
              </p>
              <p className="text-xs text-purple-600 mt-1">
                {submittedSubmittals} submitted • {approvedSubmittals} approved
                • {uploadedSubmittalsKpi} uploaded
              </p>
              {/* Inline Submittal status overview */}
              <div className="mt-2 text-[10px] text-gray-500 flex flex-wrap gap-x-2 gap-y-1">
                <span>Submitted {submittedSubmittals}</span>
                <span>Approved {approvedSubmittals}</span>
                <span>Rejected {rejectedSubmittals}</span>
                <span>Uploaded {uploadedSubmittalsKpi}</span>
              </div>
            </div>
            <TrendingUp className="w-12 h-12 text-purple-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Inspection + NCR mini charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Last 7 days inspections */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Inspections – Last 7 Days
          </h3>
          {isLoading ? (
            <LoadingSpinner size="small" className="h-16" />
          ) : last7 && last7.length > 0 ? (
            <div className="flex items-end space-x-2 h-24">
              {last7.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full rounded-t bg-blue-500 transition-all"
                    style={{
                      height: `${((d.count || 0) / last7Max) * 100}%`,
                    }}
                  />
                  <span className="mt-1 text-[10px] text-gray-500">
                    {d.date_label}
                  </span>
                  <span className="text-[10px] text-gray-700">
                    {d.count || 0}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 mt-2">
              No inspections in the last 7 days.
            </p>
          )}
        </div>

        {/* NCRs – Last 7 days */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
            <AlertCircle className="w-4 h-4 mr-2" />
            NCRs – Last 7 Days
          </h3>
          {isLoading ? (
            <LoadingSpinner size="small" className="h-16" />
          ) : ncrLast7 && ncrLast7.length > 0 ? (
            <div className="flex items-end space-x-2 h-24">
              {ncrLast7.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full rounded-t bg-red-500 transition-all"
                    style={{
                      height: `${((d.count || 0) / ncrLast7Max) * 100}%`,
                    }}
                  />
                  <span className="mt-1 text-[10px] text-gray-500">
                    {d.date_label}
                  </span>
                  <span className="text-[10px] text-gray-700">
                    {d.count || 0}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 mt-2">
              No NCRs logged in the last 7 days.
            </p>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left side: projects + activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Projects */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  Active Projects
                </h3>
                <p className="text-sm text-gray-500">
                  Overview of ongoing QA/QC work
                </p>
              </div>
            </div>

            {isLoading ? (
              <LoadingSpinner size="small" className="h-32" />
            ) : projects && projects.length > 0 ? (
              <div className="space-y-4">
                {projectList
                  .filter((p) => normalizeStatus(p.status) === "active")
                  .slice(0, 3)
                  .map((project) => (
                    <div
                      key={project.id}
                      onClick={() => handleOpenProject(project)}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold text-gray-800">
                            {project.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {project.client}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${statusColorFn(
                            project.status,
                          )}`}
                        >
                          {project.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-xs mb-3">
                        <div>
                          <p className="text-gray-500">Budget</p>
                          <p className="font-semibold text-gray-800">
                            {project.budget
                              ? `$${(project.budget / 1_000_000).toFixed(0)}M`
                              : "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Start Date</p>
                          <p className="font-semibold text-gray-800">
                            {project.start_date
                              ? new Date(
                                  project.start_date,
                                ).toLocaleDateString()
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Completion</p>
                          <p className="font-semibold text-gray-800">
                            {project.completion_percentage ?? 0}%
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-semibold text-gray-800">
                            {project.completion_percentage ?? 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-blue-600"
                            style={{
                              width: `${project.completion_percentage || 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Building className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500">No projects found</p>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Recent Activity
            </h3>

            {isLoading ? (
              <LoadingSpinner size="small" className="h-32" />
            ) : activityFeed && activityFeed.length > 0 ? (
              <div className="space-y-3">
                {activityFeed.slice(0, 5).map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:shadow-md transition"
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        activity.type === "ncr"
                          ? "bg-red-100"
                          : activity.type === "inspection"
                            ? "bg-blue-100"
                            : activity.type === "rfi"
                              ? "bg-yellow-100"
                              : "bg-purple-100"
                      }`}
                    >
                      {activity.type === "ncr" && (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      {activity.type === "inspection" && (
                        <ClipboardCheck className="w-5 h-5 text-blue-600" />
                      )}
                      {activity.type === "rfi" && (
                        <MessageSquare className="w-5 h-5 text-yellow-600" />
                      )}
                      {activity.type === "document" && (
                        <FileText className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold">{activity.user}</span>{" "}
                        {activity.action} {activity.item}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {activity.project_name} •{" "}
                        {activity.timestamp
                          ? new Date(activity.timestamp).toLocaleString()
                          : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Right side: urgent + quick actions + KPIs */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Wrench className="w-5 h-5 mr-2" />
              Quick Actions
            </h3>
            <div className="space-y-2">
              {canCreateProject && (
                <button
                  onClick={() => onQuickAction && onQuickAction("project")}
                  className="w-full px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-sm font-medium flex items-center justify-between"
                >
                  <span>New Project</span>
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* KPIs */}
          {kpis && (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Performance Metrics
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Completion Rate</span>
                  <span className="font-bold text-green-600">
                    {kpis.completion_rate}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Avg NCR Resolution
                  </span>
                  <span className="font-bold text-blue-600">
                    {kpis.avg_ncr_resolution} days
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Test Pass Rate</span>
                  <span className="font-bold text-purple-600">
                    {kpis.test_pass_rate}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Approval Time</span>
                  <span className="font-bold text-orange-600">
                    {kpis.avg_approval_time} days
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
